'use client'
import { useAddToCart } from '@/hooks/react-query/cart/use-cart'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent, TTrackingEcommerce } from '@/lib/gtm/type'
import { trackPixelEvent } from '@/lib/tracking/track-pixel'
import { ProductFormatted, VariantMapped } from '@/types/product'
import { createContext, useContext, useEffect, useState } from 'react'

interface ProductCardContextType {
  product: ProductFormatted
  selectedColor: string
  onChangeColorVariant: (variant: TSelectedColorVariant) => void
  selectedColorVariant: TSelectedColorVariant
  handleAddToCart: (variant: VariantMapped) => Promise<void>
  isPendingAddToCart: boolean
  trackingClickProduct: () => void
}

type TSelectedColorVariant = { color: string; variants: VariantMapped[] }

const ProductCardContext = createContext<ProductCardContextType | undefined>(
  undefined
)

export const ProductCardProvider = ({
  children,
  product,
}: {
  children: React.ReactNode
  product: ProductFormatted
}) => {
  // state
  const { mutateAsync: addToCart, isPending: isPendingAddToCart } =
    useAddToCart()

  const trackEvent = useTrackEvent<Partial<TTrackingEcommerce>>()

  const [selectedColorVariant, setSelectedColorVariant] =
    useState<TSelectedColorVariant>({} as TSelectedColorVariant)

  // func
  const onChangeColorVariant = (variant: TSelectedColorVariant) => {
    setSelectedColorVariant(variant)
  }

  const handleAddToCart = async (variant: VariantMapped) => {
    if (isPendingAddToCart) return

    await addToCart({
      payload: {
        product_id: variant.product_id,
        quantity: 1,
        options: {
          id: variant.id,
        },
      },
      toastOptions: {
        productName: product.title || '',
        variantName: `${variant?.color || ''} / ${variant?.size || ''}`,
        regularPrice: product.regular_price,
        comparePrice: product.compare_price || 0,
        imageUrl: variant.thumbnail || product.default_thumbnail || '',
      },
    })

    const items = selectedColorVariant?.variants.map((variant) => {
      return {
        item_id: variant.id,
        item_name: product.title,
        price: variant.regular_price,
        quantity: variant.quantity,
        discount: product.sale_percent,
        item_brand: 'Coolmate',
      }
    })

    trackEvent({
      event: ETrackingEvent.AddToCart,
      ecommerce: {
        product_id: product.id,
        product_name: product.title,
        product_price: variant.regular_price,
        product_quantity: 1,
        product_url: product.href,
        product_image: variant.thumbnail,
        product_variant: variant.title || variant.color,
        product_discount: product.sale_percent,
        product_rating: product?.review?.ratingValue || 0,
        items: items,
      },
    })
    const ids = product.id + selectedColorVariant.color.replace('-', '')

    trackPixelEvent('AddToCart', {
      content_name: product.title.replace('/\n/', '').trim(),
      content_category: product?.category,
      content_ids: [ids],
      content_type: 'product',
      value: Number(product.regular_price),
      currency: 'VND',
    })

    trackPixelEvent('InitiateCheckout', {
      content_ids: [ids],
      eventref: '',
    })
  }

  // effect
  useEffect(() => {
    if (product) {
      setSelectedColorVariant({
        color: Object.keys(product.mapped_variants)[0],
        variants:
          product.mapped_variants[Object.keys(product.mapped_variants)[0]],
      })
    }
  }, [product])

  const trackingClickProduct = () => {
    const items = selectedColorVariant?.variants.map((variant) => {
      return {
        item_id: variant.id,
        item_name: product.title,
        price: variant.regular_price,
        quantity: variant.quantity,
        discount: product.sale_percent,
        item_brand: 'Coolmate',
      }
    })

    const eventData = {
      event: ETrackingEvent.SelectItem,
      ecommerce: {
        product_id: product.id,
        product_name: product.title,
        product_price: product.regular_price,
        product_quantity: 1,
        product_url: product.href,
        product_image: product.default_thumbnail,
        product_variant: selectedColorVariant.color,
        product_discount: product.sale_percent,
        product_rating: product?.review?.ratingValue || 0,
        items: items,
      },
    }

    const isGoodPrice = product?.tags?.some((tag) =>
      ['goodprice', 'Good Price'].includes(tag)
    )

    if (isGoodPrice) {
      trackEvent({
        event: ETrackingEvent.GoodPrice,
      })
    }
    trackEvent(eventData)
  }

  // render
  return (
    <ProductCardContext.Provider
      value={{
        product,
        selectedColor: selectedColorVariant.color,
        onChangeColorVariant,
        selectedColorVariant,
        handleAddToCart,
        isPendingAddToCart,
        trackingClickProduct,
      }}
    >
      {children}
    </ProductCardContext.Provider>
  )
}

export const useProductCardContext = () => {
  const context = useContext(ProductCardContext)
  if (!context) {
    throw new Error(
      'useProductCardContext must be used within a ProductCardProvider'
    )
  }
  return context
}
